/* ============================================================================
   CL PRICE SYNC
   ----------------------------------------------------------------------------
   Keeps a single combined price ("variant base + Easify add-ons") in sync
   across both the main product page and the lens modal.

   Update sources:
     1. variant:change on <product-rerender>
          Variant changed (outer <select> or inner frame-size cards).
          Dispatched by lens-module-mw.js after the section fetch.

     2. MutationObserver on .tpo_total-additional-price (Easify's visible total)
          Easify recomputed its add-on total. Easify writes innerHTML on this
          span — childList + characterData mutations fire reliably.
          We DO NOT observe the hidden input: Easify uses node.value=…
          (property setter), which never triggers attribute mutations, so an
          attribute observer would silently miss every recalc.

     3. Click + change on the modal form
          Belt-and-braces fallback. Easify wires its option clicks/changes on
          the form's children; after each, we re-read the span on the next
          microtask. Catches edge cases where the span is detached/replaced
          rather than mutated in place (file uploads, conditional logic).

     4. easify:loaded
          First paint after Easify finishes init (covers cases where Easify
          finishes after our initial wire-up).

   Render targets (kept identical):
     A. Main-page price node — first .price element inside <product-rerender>
     B. Modal price slot     — a <div class="cl-lens-modal__price"> inserted
                               by this script at the top of .cl-lens-modal__body.
                               Idempotent — created once, never duplicated.

   Money handling:
     Variant prices (Shopify variant JSON) -> integer cents.
     Easify span text                      -> formatted string in shop currency.
     parseMoneyToCents() handles "INR 4,500.00", "₹4,500.00", "$4,500",
     and EU-style "4.500,00" by detecting the decimal separator from the tail.
   ============================================================================ */
(function () {
  'use strict';

  var MONEY_FORMAT =
    (window.Shopify && window.Shopify.money_format) ||
    (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.moneyFormat) ||
    '{{amount}}';

  /* -------------------------------------------------------------------------
   * Money helpers
   * ----------------------------------------------------------------------- */

  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, MONEY_FORMAT);
    }
    var amount = (cents / 100).toFixed(2);
    return MONEY_FORMAT.replace(/\{\{\s*amount[^}]*\}\}/g, amount);
  }

  /**
   * Parse formatted money text -> integer cents.
   * Strategy: keep digits + last separator as decimal; everything else dropped.
   */
  function parseMoneyToCents(text) {
    if (text == null) return 0;
    var clean = String(text).replace(/\s/g, '').trim();
    if (!clean) return 0;

    clean = clean.replace(/[^\d.,\-]/g, '');
    if (!clean) return 0;

    var lastDot = clean.lastIndexOf('.');
    var lastComma = clean.lastIndexOf(',');
    var decimalSep = null;
    if (lastDot > lastComma && /\.\d{1,2}$/.test(clean)) decimalSep = '.';
    else if (lastComma > lastDot && /,\d{1,2}$/.test(clean)) decimalSep = ',';

    var normalized;
    if (decimalSep) {
      var thousandSep = decimalSep === '.' ? ',' : '.';
      normalized = clean.split(thousandSep).join('').replace(decimalSep, '.');
    } else {
      normalized = clean.replace(/[.,]/g, '');
    }

    var num = parseFloat(normalized);
    if (!isFinite(num)) return 0;
    return Math.round(num * 100);
  }

  /* -------------------------------------------------------------------------
   * Locate scaffolding
   * ----------------------------------------------------------------------- */

  function getRerender(wrapper) {
    return wrapper.closest('product-rerender') || document.querySelector('product-rerender');
  }

  function getMainPriceNode(rerender) {
    if (!rerender) return null;
    return (
      rerender.querySelector('price-list') ||
      rerender.querySelector('.price') ||
      rerender.querySelector('[data-product-price]')
    );
  }

  function getModal(sectionId) {
    return document.getElementById('cl-lens-modal-' + sectionId);
  }

  function getModalForm(sectionId) {
    var modal = getModal(sectionId);
    return modal ? modal.querySelector('form.shopify-product-form') : null;
  }

  /**
   * Easify renders the visible add-on total inside .tpo_total-additional-price.
   * There can be 2 (currency-converter clones desktop + mobile) — they always
   * show the same value, so the FIRST one is fine.
   */
  function getEasifyTotalSpan(modal) {
    if (!modal) return null;
    return modal.querySelector('.tpo_total-additional-price');
  }

  function getVariantsFromWrapper(wrapper) {
    var script = wrapper.querySelector('script[data-cl-lens-variants]');
    if (!script) return [];
    try {
      var parsed = JSON.parse(script.textContent);
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      return [];
    }
  }

  function findVariantById(variants, id) {
    for (var i = 0; i < variants.length; i++) {
      if (String(variants[i].id) === String(id)) return variants[i];
    }
    return null;
  }

  /* -------------------------------------------------------------------------
   * Modal ATC button price span — injected once, survives re-renders
   * (modal lives outside <product-rerender> after the portal, so its DOM
   * is not replaced by variant fetches)
   * ----------------------------------------------------------------------- */

  function ensureModalAtcPriceSpan(sectionId) {
    var modal = getModal(sectionId);
    if (!modal) return null;

    var existing = modal.querySelector('[data-cl-atc-price]');
    if (existing) return existing;

    var btn = modal.querySelector('[name="add"]') ||
              modal.querySelector('form.shopify-product-form button[type="submit"]');
    if (!btn) return null;

    btn.classList.add('cl-lens-modal-atc-btn');

    var span = document.createElement('span');
    span.setAttribute('data-cl-atc-price', '');
    btn.appendChild(span);
    return span;
  }

  /* -------------------------------------------------------------------------
   * Render
   * ----------------------------------------------------------------------- */

  function renderCombined(state) {
    var totalCents = state.variantPriceCents + state.addOnCents;
    var formatted = formatMoney(totalCents);

    if (state.mainPriceNode) {
      state.mainPriceNode.setAttribute('data-cl-combined-price', String(totalCents));
      var moneyChild = state.mainPriceNode.querySelector(
        '.price-item--regular, .price__regular .money, [data-money], .money'
      );
      if (moneyChild) {
        moneyChild.textContent = formatted;
      } else {
        state.mainPriceNode.textContent = formatted;
      }
    }

    if (state.atcPriceSpan) {
      state.atcPriceSpan.textContent = formatted;
    }
  }

  /* -------------------------------------------------------------------------
   * Easify add-on total — read & observe
   * ----------------------------------------------------------------------- */

  function readEasifyAddOnCents(modal) {
    var span = getEasifyTotalSpan(modal);
    if (!span) return 0;

    // The span is wrapped in .tpo_total-additional-price-wrapper, which
    // toggles .tpo_hidden when no add-ons are active. Treat hidden as 0.
    var wrapper = span.closest('.tpo_total-additional-price-wrapper');
    if (wrapper && wrapper.classList.contains('tpo_hidden')) return 0;

    return parseMoneyToCents(span.textContent);
  }

  /* -------------------------------------------------------------------------
   * Wire one lens-actions wrapper
   * ----------------------------------------------------------------------- */

  function wire(wrapper) {
    if (wrapper.dataset.clPriceSyncWired === '1') return;
    wrapper.dataset.clPriceSyncWired = '1';

    var sectionId = wrapper.getAttribute('data-cl-lens-section-id');
    if (!sectionId) return;

    var rerender = getRerender(wrapper);
    if (!rerender) return;

    var variants = getVariantsFromWrapper(wrapper);
    var initialVariantId = wrapper.getAttribute('data-cl-lens-current-variant-id');
    var initialVariant = findVariantById(variants, initialVariantId) || variants[0] || null;

    var state = {
      mainPriceNode: getMainPriceNode(rerender),
      atcPriceSpan: ensureModalAtcPriceSpan(sectionId),
      variantPriceCents: initialVariant ? Number(initialVariant.price) || 0 : 0,
      addOnCents: readEasifyAddOnCents(getModal(sectionId))
    };

    function recalcAddOnsAndRender() {
      var fresh = readEasifyAddOnCents(getModal(sectionId));
      if (fresh === state.addOnCents) return;
      state.addOnCents = fresh;
      renderCombined(state);
    }

    /* --- Source 1: variant:change ---------------------------------------- */
    rerender.addEventListener('variant:change', function (event) {
      var variant = event.detail && event.detail.variant;
      if (!variant) return;
      state.variantPriceCents = Number(variant.price) || 0;
      state.mainPriceNode = getMainPriceNode(rerender);
      renderCombined(state);
    });

    /* --- Source 2: MutationObserver on Easify's visible total ------------ */
    var spanObserver = null;
    function attachSpanObserver() {
      if (spanObserver) {
        spanObserver.disconnect();
        spanObserver = null;
      }
      var span = getEasifyTotalSpan(getModal(sectionId));
      if (!span) return;

      spanObserver = new MutationObserver(recalcAddOnsAndRender);
      spanObserver.observe(span, {
        childList: true,
        characterData: true,
        subtree: true
      });

      var w = span.closest('.tpo_total-additional-price-wrapper');
      if (w) {
        spanObserver.observe(w, {
          attributes: true,
          attributeFilter: ['class']
        });
      }
    }
    attachSpanObserver();

    /* --- Source 3: form change/click fallback ---------------------------- */
    var modalForm = getModalForm(sectionId);
    if (modalForm) {
      // Easify mutates the span synchronously inside its change handlers,
      // but some option types update on a microtask. setTimeout(0) catches
      // both. Bubble phase = runs after Easify's own listeners.
      var deferRecalc = function () { setTimeout(recalcAddOnsAndRender, 0); };
      modalForm.addEventListener('change', deferRecalc);
      modalForm.addEventListener('click', deferRecalc);
    }

    /* --- Source 4: easify:loaded ----------------------------------------- */
    document.addEventListener('easify:loaded', function () {
      attachSpanObserver();
      recalcAddOnsAndRender();
    });

    // First paint
    renderCombined(state);
  }

  /* -------------------------------------------------------------------------
   * Init
   * ----------------------------------------------------------------------- */

  function init() {
    document.querySelectorAll('[data-cl-lens-actions]').forEach(wire);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();